/* MNL Immersion — presentation only. Native controls and existing reveal owners win. */
(() => {
  'use strict';
  if (window.MNLImmersion) { window.MNLImmersion.scan(document); return; }
  if (/^\/(?:cart|checkout|checkouts|account)(?:\/|$)/.test(location.pathname)) return;
  const cards = '.mnl-sh__journal-grid > article, .at-journal__story, .mnl-blog__fcard, ' +
    '.mnl-blog__card, .mnl-rb__card, .mnl-coll__card, .mnl-art__nav-card, .mnl-gl__entry, ' +
    '.mnl-prod__trust-item, .mnl-prod__rv-card, .mnl-product-range__card, ' +
    '.mnl-ra__editorial-visual, .mnl-ra__related-card';
  const choices = '.mnl-prod__swatch, .mnl-prod__thumb, .mnl-prod__tab';
  const forbidden = '#mnl-overlay, #mnl-configurator, [data-mnl-studio], ' +
    '[role="dialog"], [aria-modal="true"], dialog, [inert], [aria-hidden="true"], ' +
    '[class*="modal"], [class*="lightbox"], [data-mnl-chat], #mnl-cart-studio';
  const owners = '[data-at-reveal], [data-at-enter], .at-enter, [data-mnl-reveal], ' +
    '[data-mnl-photo-reveal], [data-mnl-fade], [data-mnl-stagger], .mnl-reveal, ' +
    '.mnl-vu, .mnl-fade-img, .mnl-sh__step, .mnl-sh__section-head, ' +
    '.at-specs > div, .at-ex__heading, .at-ex__tabs, .at-sports > a, ' +
    '.at-formulas__header, .at-formulas__card, .at-practice__heading, .at-campaign-card, ' +
    '.mnl-product-guide__header, .mnl-product-guide__visual, .mnl-product-guide__steps > li, .mnl-product-guide__choices';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const seen = new WeakSet(), observed = new Set(), marked = new Set(), transitions = new WeakMap();
  const effects = new Map(), heroes = new Map(), pendingChoices = new Set(), selectedGroups = new WeakMap();
  let observer = null, heroObserver = null, frame = 0, suspended = false, destroyed = false;
  const allowed = () => !destroyed && !suspended && !document.hidden && !reduced.matches;
  function safe(element) {
    return element?.isConnected && !!element.closest('#main-content, main') &&
      !element.closest(forbidden) && !element.closest(owners);
  }
  const entranceSafe = element => safe(element) && !element.querySelector(owners);
  function safeIcon(icon) {
    const card = icon.closest('[data-mnl-immersion-card]'), owner = icon.closest(owners);
    const decoration = icon.matches('span[aria-hidden="true"], svg[aria-hidden="true"]');
    const blocked = icon.parentElement.closest(forbidden) || icon.matches(forbidden.replace('[aria-hidden="true"], ', '')) ||
      (icon.matches('[aria-hidden="true"]') && !decoration);
    if (!card || !safe(card) || icon.matches(owners) || blocked || icon.querySelector(owners) || !imageAvailable(icon)) return false;
    // A single existing caption entrance may own the parent. Polish only its
    // distinct arrow span, never replace or animate that caption's transform.
    return !owner || (icon.parentElement === owner && owner.matches('.mnl-sh__article-label.mnl-reveal') && !owner.parentElement.closest(owners));
  }
  function visible(element) {
    if (!element.getClientRects().length || element.closest('[hidden], details:not([open])')) return false;
    const rect = element.getBoundingClientRect();
    return rect.height > 0 && rect.height <= 720 && rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  }
  function cancelEffect(element) {
    const effect = effects.get(element);
    if (effect) { effect.cancel(); effects.delete(element); observer?.unobserve(element); observed.delete(element); }
  }
  function play(element, frames, duration, allowFocused = false) {
    if (!allowed() || !entranceSafe(element) || !visible(element) || (!allowFocused && element.contains(document.activeElement)) || typeof element.animate !== 'function') return;
    cancelEffect(element);
    try {
      const effect = element.animate(frames, {duration, easing: 'cubic-bezier(.22,1,.36,1)'});
      effects.set(element, effect);
      if (observer) { seen.add(element); observed.add(element); observer.observe(element); }
      const finish = () => {
        if (effects.get(element) !== effect) return;
        effects.delete(element); observer?.unobserve(element); observed.delete(element);
      };
      Promise.resolve(effect.finished).then(finish, finish);
      return effect;
    } catch (_) { /* Visible by default, even without Web Animations. */ }
  }
  function mark(element, attribute) {
    element.setAttribute(attribute, ''); marked.add(element);
  }
  function unmark(element) {
    for (const attribute of ['data-mnl-immersion-card', 'data-mnl-immersion-image', 'data-mnl-immersion-icon', 'data-mnl-immersion-choice', 'data-mnl-immersion-depth-active']) element.removeAttribute(attribute);
    marked.delete(element);
  }
  function imageAvailable(image) {
    // Do not replace any theme-owned image transition with this hover effect.
    if (transitions.has(image)) return transitions.get(image);
    const style = getComputedStyle(image);
    const available = !style.transitionDuration.split(',').some(duration => parseFloat(duration) > 0);
    transitions.set(image, available); return available;
  }
  function ensureObservers() {
    if (!allowed() || !('IntersectionObserver' in window)) return;
    if (!observer) observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!allowed()) return;
        if (!entry.isIntersecting) { cancelEffect(entry.target); continue; }
        if (!entranceSafe(entry.target)) {
          for (const element of [...marked]) if ((element === entry.target || entry.target.contains(element)) &&
            !safe(element) && !(element.hasAttribute('data-mnl-immersion-icon') && safeIcon(element))) unmark(element);
          cancelEffect(entry.target);
          observer.unobserve(entry.target); observed.delete(entry.target); continue;
        }
        if (seen.has(entry.target)) continue;
        seen.add(entry.target);
        const effect = play(entry.target, [{opacity: .82, transform: 'translateY(8px)'}, {opacity: 1, transform: 'translateY(0)'}], 380);
        if (!effect) { observer.unobserve(entry.target); observed.delete(entry.target); }
      }
    }, {threshold: .1});
    if (!heroObserver) heroObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const hero = heroes.get(entry.target);
        if (!hero) continue;
        hero.visible = entry.isIntersecting;
        if (!hero.visible) resetHero(hero);
      }
    }, {threshold: .1});
  }
  function resetHero(hero) {
    hero.point = null; hero.bounds = null;
    hero.image.style.removeProperty('--mnl-immersion-x');
    hero.image.style.removeProperty('--mnl-immersion-y');
    if (frame && !pendingChoices.size && ![...heroes.values()].some(item => item.point)) {
      cancelAnimationFrame(frame); frame = 0;
    }
  }
  function schedule() {
    if (frame || !allowed()) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (!allowed()) return;
      for (const hero of heroes.values()) {
        if (!hero.point || !hero.visible || !fine.matches || !safe(hero.element) || !safe(hero.image)) continue;
        hero.image.style.setProperty('--mnl-immersion-x', `${hero.point.x.toFixed(2)}px`);
        hero.image.style.setProperty('--mnl-immersion-y', `${hero.point.y.toFixed(2)}px`);
        hero.point = null;
      }
      for (const choice of pendingChoices) {
        const group = choice.closest('[role="radiogroup"], [role="tablist"], .mnl-prod__thumbs') || choice.parentElement;
        if (choice.matches('.is-active, [aria-checked="true"], [aria-selected="true"]') &&
            !choice.matches(':disabled, [aria-disabled="true"]') && selectedGroups.get(group) !== choice) {
          selectedGroups.set(group, choice);
          play(choice, [{opacity: .72}, {opacity: 1}], 180, true);
        }
      }
      pendingChoices.clear();
    });
  }
  function attachHero(element) {
    if (!heroObserver || heroes.has(element) || !safe(element)) return;
    // Never translate an integrated raster banner containing headings or buttons.
    const image = element.querySelector('[data-mnl-immersion-depth]') ||
      (element.matches('.mnl-sh__hero-product') ? element.querySelector(':scope > img') : null);
    if (!image || !safe(image) || !imageAvailable(image)) return;
    const hero = {element, image, visible: visible(element), point: null, bounds: null};
    hero.move = event => {
      if (!allowed() || !fine.matches || event.pointerType !== 'mouse' || !hero.visible ||
          (image.tagName === 'IMG' && (!image.complete || !image.naturalWidth))) return;
      hero.bounds ||= element.getBoundingClientRect();
      if (!hero.bounds.width || !hero.bounds.height) return;
      const x = Math.max(-1, Math.min(1, (event.clientX - hero.bounds.left) / hero.bounds.width * 2 - 1));
      const y = Math.max(-1, Math.min(1, (event.clientY - hero.bounds.top) / hero.bounds.height * 2 - 1));
      hero.point = {x: x * 5, y: y * 3}; schedule();
    };
    hero.leave = () => resetHero(hero);
    element.addEventListener('pointermove', hero.move, {passive: true});
    element.addEventListener('pointerleave', hero.leave, {passive: true});
    heroes.set(element, hero); mark(image, 'data-mnl-immersion-depth-active');
    heroObserver?.observe(element);
  }
  function scan(scope = document) {
    if (destroyed) return;
    ensureObservers();
    for (const element of [...marked]) {
      if (!safe(element) && !(element.hasAttribute('data-mnl-immersion-icon') && safeIcon(element))) { cancelEffect(element); unmark(element); }
      else if (!entranceSafe(element)) cancelEffect(element);
    }
    const find = selector => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
    for (const element of find(cards)) {
      if (!safe(element)) continue;
      mark(element, 'data-mnl-immersion-card');
      if (!element.matches('.mnl-ra__editorial-visual, .mnl-ra__related-card, .mnl-product-range__card')) {
        for (const image of element.querySelectorAll('img')) if (safe(image) && imageAvailable(image)) mark(image, 'data-mnl-immersion-image');
      }
      for (const icon of element.querySelectorAll('.mnl-sh__article-label > span, .mnl-prod__trust-item > svg, .mnl-art__nav-label')) if (safeIcon(icon)) mark(icon, 'data-mnl-immersion-icon');
      if (!entranceSafe(element)) continue;
      if (seen.has(element) || observed.has(element)) continue;
      // First paint and LCP are never staged. Only unseen, below-fold cards enter.
      if (visible(element) || element.querySelector('img[fetchpriority="high"], img[loading="eager"]')) { seen.add(element); continue; }
      if (allowed() && observer) { observed.add(element); observer.observe(element); }
    }
    for (const element of find(choices)) if (safe(element)) {
      mark(element, 'data-mnl-immersion-choice');
      if (element.matches('.is-active, [aria-checked="true"], [aria-selected="true"]')) {
        selectedGroups.set(element.closest('[role="radiogroup"], [role="tablist"], .mnl-prod__thumbs') || element.parentElement, element);
      }
    }
    for (const element of find('.mnl-sh__hero-product, [data-mnl-immersion-hero]')) attachHero(element);
    for (const element of observed) if (!element.isConnected) { observer?.unobserve(element); observed.delete(element); }
    for (const [element, hero] of heroes) if (!safe(element) || !safe(hero.image)) detachHero(hero);
    for (const element of marked) if (!element.isConnected) marked.delete(element);
  }
  function detachHero(hero) {
    resetHero(hero); heroObserver?.unobserve(hero.element);
    hero.image.removeAttribute('data-mnl-immersion-depth-active');
    hero.element.removeEventListener('pointermove', hero.move);
    hero.element.removeEventListener('pointerleave', hero.leave); heroes.delete(hero.element);
  }
  function stop() {
    if (frame) cancelAnimationFrame(frame); frame = 0; pendingChoices.clear();
    for (const effect of effects.values()) effect.cancel(); effects.clear();
    for (const hero of heroes.values()) resetHero(hero);
    observer?.disconnect(); heroObserver?.disconnect(); observer = null; heroObserver = null; observed.clear();
  }
  function resume() {
    if (!allowed()) return;
    scan(document);
    for (const hero of heroes.values()) { hero.visible = visible(hero.element); heroObserver?.observe(hero.element); }
  }
  function onChoice(event) {
    const choice = event.target.closest?.(choices);
    if (!choice || !allowed() || !safe(choice) || choice.matches(':disabled, [aria-disabled="true"]')) return;
    pendingChoices.add(choice); schedule();
  }
  function onFocus(event) {
    for (const element of effects.keys()) if (element.contains(event.target)) cancelEffect(element);
  }
  function onPreference() { if (!allowed()) stop(); else resume(); }
  function onFine() { if (!fine.matches) for (const hero of heroes.values()) resetHero(hero); }
  function onVisibility() { if (document.hidden) stop(); else resume(); }
  function onPageHide() { suspended = true; stop(); }
  function onPageShow() { suspended = false; resume(); }
  function onResize() { for (const hero of heroes.values()) resetHero(hero); }
  function onLoad(event) { scan(event.target); }
  function onUnload(event) {
    for (const element of effects.keys()) if (event.target.contains(element)) cancelEffect(element);
    for (const element of observed) if (event.target.contains(element)) { observer?.unobserve(element); observed.delete(element); }
    for (const [element, hero] of heroes) if (event.target.contains(element)) detachHero(hero);
    for (const element of marked) if (event.target.contains(element)) {
      unmark(element);
    }
  }
  const listeners = [
    [document, 'click', onChoice], [document, 'focusin', onFocus], [document, 'visibilitychange', onVisibility],
    [document, 'shopify:section:load', onLoad], [document, 'shopify:section:unload', onUnload],
    [window, 'pagehide', onPageHide], [window, 'pageshow', onPageShow], [window, 'resize', onResize]
  ];
  for (const [target, event, handler] of listeners) target.addEventListener(event, handler, {passive: true});
  reduced.addEventListener('change', onPreference); fine.addEventListener('change', onFine);
  window.MNLImmersion = {
    scan,
    snapshot: () => ({observed: observed.size, effects: effects.size, heroes: heroes.size, pendingFrame: !!frame, reduced: reduced.matches, suspended}),
    destroy() {
      if (destroyed) return;
      destroyed = true; stop();
      for (const hero of [...heroes.values()]) detachHero(hero);
      for (const [target, event, handler] of listeners) target.removeEventListener(event, handler);
      reduced.removeEventListener('change', onPreference); fine.removeEventListener('change', onFine);
      for (const element of [...marked]) unmark(element);
      marked.clear();
      document.removeEventListener('DOMContentLoaded', boot);
      delete window.MNLImmersion;
    }
  };
  function boot() { scan(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true}); else boot();
})();
